/**
 * Loading webfonts and knowing when they have actually arrived.
 *
 * The trap this module exists to close: `document.fonts.check()` does not
 * answer "is this family loaded". It answers "can this text be painted", and
 * an unknown family paints fine with a fallback — so it returns **true** for a
 * font that does not exist, and **false** for a declared face that has not been
 * fetched yet. Every `if (!document.fonts.check(...)) await load(...)` guard
 * built on the intuitive reading therefore skips the load entirely.
 */

// A dead or slow font CDN must not hold a screen shut.
const FONT_LOAD_TIMEOUT_MS = 5000;

const afterTimeout = async <T>(value: T, ms: number): Promise<T> =>
  await new Promise<T>((resolve) => setTimeout(() => resolve(value), ms));

const unquote = (family: string): string =>
  family.replace(/^['"]|['"]$/g, '').toLowerCase();

/**
 * Whether a family is present in the document *and* fetched.
 *
 * This is the check `document.fonts.check()` is mistaken for: it asks the font
 * set what it holds instead of asking whether some text could be painted.
 */
export const isFamilyLoaded = (family: string): boolean => {
  const wanted = unquote(family);
  let loaded = false;
  document.fonts.forEach((face) => {
    if (unquote(face.family) === wanted && face.status === 'loaded') {
      loaded = true;
    }
  });
  return loaded;
};

/**
 * Add a stylesheet and wait for the browser to have parsed it.
 *
 * `document.fonts.load()` looks for an `@font-face` rule the document already
 * knows; it does not wait for a stylesheet still in flight. Appending the link
 * and calling it in the same tick resolves against nothing at all.
 */
const awaitStylesheet = async (href: string): Promise<void> => {
  const existing = document.querySelector<HTMLLinkElement>(
    `link[href="${href}"]`
  );

  if (existing?.sheet) return;

  const link = existing ?? document.createElement('link');
  if (!existing) {
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  await new Promise<void>((resolve) => {
    link.addEventListener('load', () => resolve(), { once: true });
    // A stylesheet that 404s still has to let the caller through.
    link.addEventListener('error', () => resolve(), { once: true });
  });
};

/**
 * Load a family that a stylesheet declares (a Google Fonts CSS URL, say) and
 * resolve once it is usable. False means it timed out or never arrived — the
 * caller carries on either way, but it can now say which happened.
 */
export const loadFontFromStylesheet = async (
  stylesheetUrl: string,
  family: string,
  face: string = `normal 400 16px '${family}'`
): Promise<boolean> => {
  try {
    if (isFamilyLoaded(family)) return true;

    await Promise.race([
      awaitStylesheet(stylesheetUrl),
      afterTimeout(undefined, FONT_LOAD_TIMEOUT_MS)
    ]);

    // Resolves with the faces it matched — an empty list means the descriptor
    // matched no rule, which is the failure `check()` would have hidden.
    const matched = await Promise.race([
      document.fonts.load(face),
      afterTimeout([] as FontFace[], FONT_LOAD_TIMEOUT_MS)
    ]);

    const available = matched.length > 0 || isFamilyLoaded(family);
    if (!available) {
      console.warn(`Font "${family}" did not become available`);
    }
    return available;
  } catch (error) {
    console.warn(`Failed to load the font "${family}"`, error);
    return false;
  }
};

// One request and one FontFace per family for the lifetime of the tab. Adding
// the same face again leaves duplicates in document.fonts that are never
// collected.
const availableFamilies = new Set<string>();
const inFlightFamilies = new Map<string, Promise<boolean>>();

/**
 * Load a family straight from a font file, under a name you choose. Use this
 * when you have the file URL and want the family registered without a
 * stylesheet — a preview list, for instance, where the name is an alias.
 */
export const loadFontFromFile = async (
  family: string,
  fileUrl?: string
): Promise<boolean> => {
  if (!fileUrl) return false;
  if (availableFamilies.has(family)) return true;
  if (inFlightFamilies.has(family)) {
    return await inFlightFamilies.get(family);
  }

  const loading = new FontFace(family, `url(${fileUrl})`)
    .load()
    .then((loadedFace) => {
      document.fonts.add(loadedFace);
      availableFamilies.add(family);
      return true;
    })
    .catch((error) => {
      // One unavailable file must not take the caller down with it — but say
      // so, because the alternative is text that silently reads as the
      // fallback and looks like everything around it.
      console.warn(`Could not load the font "${family}"`, error);
      inFlightFamilies.delete(family);
      return false;
    });

  inFlightFamilies.set(family, loading);
  return await loading;
};

/** Whether {@link loadFontFromFile} has this family ready, without loading it. */
export const isFileFontReady = (family: string): boolean =>
  availableFamilies.has(family);
